import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Checkbox, Input, Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faEnvelope,
  faLock,
  faMagnifyingGlass,
  faCheck,
  faChevronLeft,
  faPen,
  faShieldHalved,
  faGift,
  faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons';

import type { WalletCashOutBrandGroup } from '@share/common-types';
import { getUserWallet } from '@share/store/slices';
import { FunctionalRootState, RootState } from '@share/utils';

import {
  CODE_LENGTH,
  FAILED_STATUSES,
  TERMINAL_STATUSES,
  useWalletCashOut,
} from '../../../wallet-cash-out/use-wallet-cash-out';
import { AccountEmptyState } from '../empty-state';

import { BrandLogo, StatusBadge, statusTone } from './shared';

import { CodeBoxes } from './code-boxes';
import { Confetti } from './confetti';
import { RecentCashOuts } from './recent';

import styles from './styles.module.scss';

const GROUP_ORDER: WalletCashOutBrandGroup[] = ['GiftCard', 'PrepaidCard', 'Transfer', 'Charity'];
const GROUP_LABELS: Record<WalletCashOutBrandGroup, string> = {
  GiftCard: 'wallet.cashout.group.gift_card',
  PrepaidCard: 'wallet.cashout.group.prepaid_card',
  Transfer: 'wallet.cashout.group.transfer',
  Charity: 'wallet.cashout.group.charity',
};
const SEARCH_THRESHOLD = 8;
// Reference fee shown struck through when the cash-out is free, so members see what they save.
const REFERENCE_FEE_RATE = 0.1;
const COLLAPSED_COUNT = 6;

// Hub steps, one at a time like the transfers round trip: each choice folds into a summary tile with
// Change, and the next step opens below it. Then the review (the first time the quote and the warning
// appear), the emailed code when required, and the result.
type HubStep = 'amount' | 'option' | 'review' | 'verify' | 'done';
const HUB_STEP_LABELS: Record<HubStep, string> = {
  amount: 'my.account.hub.cashout.step.amount',
  option: 'my.account.hub.cashout.step.option',
  review: 'wallet.cashout.step.review',
  verify: 'my.account.hub.cashout.step.verify',
  done: 'wallet.cashout.step.done',
};

/**
 * Wallet cash out inside My Account. All the behaviour (options, quote, emailed code, idempotent
 * create, status, history, safe redirect to the redemption link) comes from `useWalletCashOut`, the
 * same hook the stand-alone cash-out page uses; this is only the hub layout.
 */
interface IProps {
  accountUsername: string;
}

export const AccountCashOut = ({ accountUsername }: IProps): React.ReactElement => {
  const intl = useIntl();
  const flow = useWalletCashOut();
  const {
    userWalletData, isLoading, options, history, step, setStep, amount, productId, setProductId, quote, detail,
    isBusy, isRedirecting, challenge, code, setCode, resendIn, resultRef, formatPoints, formatMoney, brandsForAmount,
    selectedBrand, selectAmount, amountError, feeHint, amountBreakdown, openDetail, handleContinue, sendCode, handleConfirm, handleRedeem,
    handleStartOver, quickAmounts,
  } = flow;

  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const userWallet = useSelector((state: FunctionalRootState) => state.loginStore.userWallet);
  const ownerEmail = useSelector((state: FunctionalRootState) => state.loginStore.user?.email);

  // The stored wallet is loaded at login and right after a cash-out is created; a cash-out that later
  // fails or is reversed gives the money back on the server. Reload it on open and when a failed one
  // is shown, so "available for bookings" matches the cash-out figures.
  const failedDetail = !!detail && FAILED_STATUSES.includes(detail.status);
  useEffect(() => {
    if (userWallet) {
      dispatch(getUserWallet(userWallet));
    }
  }, [dispatch, userWallet, failedDetail]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Partial<Record<WalletCashOutBrandGroup, boolean>>>({});

  const sections = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = term ? brandsForAmount.filter(brand => brand.name.toLowerCase().includes(term)) : brandsForAmount;
    return GROUP_ORDER
      .map(group => ({ group, brands: matching.filter(brand => (brand.group ?? 'GiftCard') === group) }))
      .filter(section => section.brands.length > 0);
  }, [brandsForAmount, search]);

  const [editing, setEditing] = useState<'amount' | 'option'>('amount');
  // The final action needs an explicit "I understand this can't be undone"; asked again for every cash-out.
  const [agreed, setAgreed] = useState(false);
  const hubStep: HubStep = step === 'form' ? editing : step === 'confirm' ? 'review' : step === 'verify' ? 'verify' : 'done';
  const finalStep: HubStep = options?.requiresVerification ? 'verify' : 'review';
  useEffect(() => {
    if (hubStep !== finalStep) {
      setAgreed(false);
    }
  }, [hubStep, finalStep]);

  // Each step replaces the previous one: bring the top of the flow back into view (long brand lists, phones).
  const stepsRef = useRef<HTMLOListElement>(null);
  const previousStep = useRef(hubStep);
  useEffect(() => {
    if (previousStep.current !== hubStep) {
      previousStep.current = hubStep;
      stepsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [hubStep]);

  // Picking an option asks for the quote straight away (the hook quotes the stored choice, so wait
  // for it to land first).
  const [quoteWanted, setQuoteWanted] = useState(false);
  useEffect(() => {
    if (quoteWanted && productId && amount != null && step === 'form' && !isBusy) {
      setQuoteWanted(false);
      handleContinue();
    }
  }, [quoteWanted, productId, amount, step, isBusy, handleContinue]);

  if (isLoading) {
    return (
      <div className={styles.cashOut}>
        <div className={styles.card}><Skeleton active paragraph={{ rows: 3 }} /></div>
        <div className={styles.card}><Skeleton active paragraph={{ rows: 5 }} /></div>
      </div>
    );
  }

  if (!options) {
    return (
      <AccountEmptyState
        variant="panel"
        icon={faCircleExclamation}
        title={intl.formatMessage({ id: 'wallet.cashout.unavailable' })}
      />
    );
  }

  const currency = options.currencyCode;
  const hubSteps: HubStep[] = options.requiresVerification
    ? ['amount', 'option', 'review', 'verify', 'done']
    : ['amount', 'option', 'review', 'done'];
  const currentStep = hubSteps.indexOf(hubStep);
  // Back from any step: one step at a time; from the result, a new cash-out starts at step 1.
  const editAmount = () => {
    setStep('form');
    setEditing('amount');
  };
  const editOption = () => {
    setStep('form');
    setEditing('option');
  };
  const goBack = () => {
    if (hubStep === 'option') {
      editAmount();
    } else if (hubStep === 'review') {
      editOption();
    } else if (hubStep === 'verify') {
      setStep('confirm');
    } else if (hubStep === 'done') {
      startOver();
    }
  };
  // Completed steps in the stepper can be reopened while the cash-out hasn't been sent yet.
  const canOpenStep = (key: HubStep, index: number) => index < currentStep && hubStep !== 'done' && key !== 'verify';
  const openStep = (key: HubStep) => {
    if (key === 'review') {
      setStep('confirm');
    } else if (key === 'option') {
      editOption();
    } else {
      editAmount();
    }
  };
  const startOver = () => {
    handleStartOver();
    setSearch('');
    setEditing('amount');
  };
  // Changing the amount keeps the chosen option when it still accepts the new amount; returns whether it did.
  const setAmountKeepingOption = (value: number | null) => {
    const keep = selectedBrand;
    selectAmount(value);
    const fits = !!keep && value != null
      && (keep.minAmount == null || value >= keep.minAmount) && (keep.maxAmount == null || value <= keep.maxAmount);
    if (keep && fits) {
      setProductId(keep.productId);
    }
    return fits;
  };
  // A chip or "Use all" is a decision: fold the amount and move on (straight back to the review when
  // the chosen option still fits). Typing in Other amount waits for Continue / Enter.
  const commitAmount = (value: number) => {
    const keptOption = setAmountKeepingOption(value);
    if (keptOption) {
      setQuoteWanted(true);
    } else {
      setEditing('option');
    }
  };
  const confirmTypedAmount = () => {
    if (amount != null && !amountError) {
      commitAmount(amount);
    }
  };
  const pickOption = (id: string) => {
    setProductId(id);
    setQuoteWanted(true);
  };
  // The code goes to the account owner's email; shown masked, like the address the code is sent to.
  const maskedOwnerEmail = ownerEmail && ownerEmail.includes('@')
    ? `${ownerEmail.slice(0, 1)}••••${ownerEmail.slice(ownerEmail.indexOf('@'))}`
    : null;
  // The minimum is offered as an amount too.
  // "All" lives inside Other amount (it fills the field); the chips are the fixed amounts.
  const fixedAmounts = quickAmounts.filter(item => !item.isAll).map(item => item.value);
  const allAmount = quickAmounts.find(item => item.isAll)?.value ?? null;
  const amountChips = options.minAmount != null && (!fixedAmounts.length || options.minAmount < fixedAmounts[0])
    && (allAmount == null || options.minAmount < allAmount)
    ? [options.minAmount, ...fixedAmounts]
    : fixedAmounts;
  const isOtherAmount = amount != null && !amountChips.includes(amount);
  const brandName = (id?: string | null) => options.brands.find(brand => brand.productId === id)?.name ?? id ?? '';
  const brandFor = (id?: string | null) => options.brands.find(brand => brand.productId === id);

  // One figure: what can be cashed out. The other balances only show when they differ from it.
  const cashable = options.eligible ? options.cashableBalance : 0;
  const walletBalance = userWalletData?.balance ?? 0;
  const showWalletFact = Math.abs(walletBalance - cashable) >= 0.01;
  const showCommissionFact = options.commissionBalance > 0 && options.commissionBalance < cashable - 0.005;
  const balanceFacts = showWalletFact || showCommissionFact ? (
    <dl className={styles.balanceFacts}>
      {showWalletFact ? (
        <div>
          <dt>{intl.formatMessage({ id: 'wallet.cashout.balance.available' })}</dt>
          <dd>{formatPoints(walletBalance)}</dd>
        </div>
      ) : null}
      {showCommissionFact ? (
        <div>
          <dt>{intl.formatMessage({ id: 'wallet.cashout.balance.commission' })}</dt>
          <dd>{formatMoney(options.commissionBalance, currency)}</dd>
        </div>
      ) : null}
    </dl>
  ) : null;
  const balanceHint = intl.formatMessage({
    id: options.commissionBalance > 0 ? 'wallet.cashout.balance.cashable.hint.commission' : 'wallet.cashout.balance.cashable.hint',
  });

  // Not eligible: the navy strip explains what the balance is. In the flow, the figure lives in the
  // amount card instead (no banner that disappears between steps).
  const balanceStrip = (
    <section className={styles.balance} aria-labelledby="account-hub-cashout-balance">
      <div className={styles.balanceMain}>
        <span id="account-hub-cashout-balance" className={styles.eyebrow}>
          {intl.formatMessage({ id: 'wallet.cashout.balance.cashable' })}
        </span>
        <span className={styles.balanceValue}>{formatPoints(cashable)}</span>
      </div>
      {balanceFacts}
      <p className={styles.balanceNote}>{balanceHint}</p>
    </section>
  );

  // Summary beside Review, Verify and Done: the quote's figures, then the cash-out's own once it exists.
  const isDone = hubStep === 'done' && !!detail;
  const showSummary = (hubStep === 'review' || hubStep === 'verify') ? !!quote : isDone;
  const summaryAmount = isDone && detail ? detail.pointsDebited : quote?.requestedAmount ?? null;
  const summaryFee = isDone && detail ? detail.feeAmount : quote?.feeAmount ?? 0;
  const summaryPayout = isDone && detail ? detail.payoutAmount : quote?.payoutAmount ?? 0;
  const summaryCurrency = isDone && detail ? detail.currencyCode : currency;
  const summaryBrand = isDone && detail ? brandFor(detail.productId) : selectedBrand;
  const dash = '—';

  // Must be ticked before the cash-out can be completed.
  const finalWarning = (
    <label className={agreed ? `${styles.consent} ${styles.consentChecked}` : styles.consent}>
      <Checkbox checked={agreed} onChange={event => setAgreed(event.target.checked)} />
      <span>
        <b>{intl.formatMessage({ id: 'my.account.hub.cashout.consent.title' })}</b>
        <span>
          {intl.formatMessage(
            { id: 'my.account.hub.cashout.consent.text' },
            {
              amount: quote ? formatPoints(quote.pointsDebited) : '',
              reward: selectedBrand?.name ?? '',
            },
          )}
        </span>
      </span>
    </label>
  );

  // A finished step folded to one row, with Change to reopen it.
  const pickedTile = (
    kind: 'amount' | 'option',
    label: string,
    value: React.ReactNode,
    meta: string | null,
    onChange: () => void,
    logo?: React.ReactNode,
  ) => (
    <div className={`${styles.picked} ${styles.enter}`} key={kind}>
      {logo}
      <div className={styles.pickedBody}>
        <span className={styles.pickedLabel}>
          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
          {label}
        </span>
        <span className={styles.pickedValue}>{value}</span>
        {meta ? <span className={styles.pickedMeta}>{meta}</span> : null}
      </div>
      <button type="button" className={styles.pickedChange} disabled={isBusy} onClick={onChange}>
        <FontAwesomeIcon icon={faPen} aria-hidden="true" />
        {intl.formatMessage({ id: 'my.account.hub.cashout.change' })}
      </button>
    </div>
  );
  const amountTile = amount != null ? pickedTile(
    'amount',
    intl.formatMessage({ id: 'my.account.hub.cashout.selected_amount' }),
    formatMoney(amount, currency),
    intl.formatMessage({ id: 'my.account.hub.cashout.picked.amount_meta' }, { available: formatPoints(cashable) }),
    editAmount,
  ) : null;
  const optionTile = selectedBrand ? pickedTile(
    'option',
    intl.formatMessage({ id: 'my.account.hub.cashout.option' }),
    selectedBrand.name,
    selectedBrand.group ? intl.formatMessage({ id: GROUP_LABELS[selectedBrand.group] }) : null,
    editOption,
    <BrandLogo brand={selectedBrand} />,
  ) : null;

  const summary = showSummary ? (
    <aside className={`${styles.card} ${styles.summary}`} aria-labelledby="account-hub-cashout-summary">
      <span id="account-hub-cashout-summary" className={styles.eyebrow}>
        {intl.formatMessage({ id: isDone ? 'my.account.hub.cashout.summary' : 'my.account.hub.cashout.quote' })}
      </span>
      <dl className={styles.summaryRows}>
        {!isDone ? (
          <div>
            <dt>{intl.formatMessage({ id: 'my.account.hub.cashout.wallet_balance' })}</dt>
            <dd>{formatPoints(walletBalance)}</dd>
          </div>
        ) : null}
        <div>
          <dt>{intl.formatMessage({ id: 'my.account.hub.cashout.selected_amount' })}</dt>
          <dd>{summaryAmount != null ? formatMoney(summaryAmount, summaryCurrency) : dash}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: 'my.account.hub.cashout.option' })}</dt>
          <dd className={styles.summaryBrand}>
            {summaryBrand ? (
              <>
                <BrandLogo brand={summaryBrand} />
                {summaryBrand.name}
              </>
            ) : dash}
          </dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: 'wallet.cashout.confirm.fee' })}</dt>
          <dd>
            {summaryAmount == null ? dash : summaryFee > 0 ? `− ${formatMoney(summaryFee, summaryCurrency)}` : (
              <span className={styles.feeFree}>
                <s>{formatMoney(summaryAmount * REFERENCE_FEE_RATE, summaryCurrency)}</s>
                <span className={styles.freeBadge}>{intl.formatMessage({ id: 'my.account.hub.cashout.fee_free' })}</span>
              </span>
            )}
          </dd>
        </div>
      </dl>
      <dl className={`${styles.summaryRows} ${styles.summaryTotals}`}>
        <div>
          <dt>{intl.formatMessage({ id: isDone ? 'my.account.hub.cashout.you_received' : 'my.account.hub.cashout.you_receive' })}</dt>
          <dd className={styles.summaryReceive}>{formatMoney(summaryPayout, summaryCurrency)}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: isDone ? 'my.account.hub.cashout.wallet_balance' : 'my.account.hub.cashout.wallet_after' })}</dt>
          <dd>
            {isDone
              ? formatPoints(walletBalance)
              : formatPoints(Math.max(0, walletBalance - (quote?.pointsDebited ?? 0)))}
          </dd>
        </div>
      </dl>
    </aside>
  ) : null;

  // The history has its own section (Wallet → Cash-out history).
  const recentCard = (
    <RecentCashOuts
      accountUsername={accountUsername}
      history={history}
      brands={options.brands}
      formatMoney={formatMoney}
      excludeCode={detail?.cashOutCode}
    />
  );

  if (!options.eligible) {
    return (
      <div className={styles.cashOut}>
        {balanceStrip}
        <AccountEmptyState
          variant="panel"
          icon={faLock}
          title={intl.formatMessage({ id: 'wallet.cashout.not_eligible' })}
          body={options.reason ?? intl.formatMessage({ id: 'wallet.cashout.not_eligible.hint' })}
        />
        {recentCard}
      </div>
    );
  }

  return (
    <div className={styles.cashOut}>
      {isRedirecting ? (
        <div className={styles.redirect} role="status" aria-live="polite">
          <div className={styles.redirectCard}>
            <span className={styles.spinner} aria-hidden="true" />
            <b>{intl.formatMessage({ id: 'wallet.cashout.redirect.title' })}</b>
            <span>{intl.formatMessage({ id: 'wallet.cashout.redirect.hint' })}</span>
          </div>
        </div>
      ) : null}

      {hubStep !== 'amount' ? (
        <button type="button" className={styles.backButton} disabled={isBusy} onClick={goBack}>
          <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
          {intl.formatMessage({ id: hubStep === 'done' ? 'my.account.hub.cashout.back_to_start' : 'wallet.cashout.back' })}
        </button>
      ) : null}

      <ol ref={stepsRef} className={styles.steps} aria-label={intl.formatMessage({ id: 'my.account.hub.cashout.steps' })}>
        {hubSteps.map((key, index) => (
          <li
            key={key}
            className={index === currentStep ? `${styles.stepItem} ${styles.stepCurrent}` : index < currentStep ? `${styles.stepItem} ${styles.stepDone}` : styles.stepItem}
            aria-current={index === currentStep ? 'step' : undefined}
          >
            {canOpenStep(key, index) ? (
              <button type="button" className={styles.stepButton} disabled={isBusy} onClick={() => openStep(key)}>
                <span className={styles.stepNumber}>✓</span>
                {intl.formatMessage({ id: HUB_STEP_LABELS[key] })}
              </button>
            ) : (
              <>
                <span className={styles.stepNumber}>{index < currentStep ? '✓' : index + 1}</span>
                {intl.formatMessage({ id: HUB_STEP_LABELS[key] })}
              </>
            )}
          </li>
        ))}
      </ol>

      <div className={summary ? styles.formGrid : styles.formSingle}>
        <div className={styles.formMain}>
          {hubStep !== 'amount' && hubStep !== 'done' && amount != null ? amountTile : null}
          {(hubStep === 'review' || hubStep === 'verify') && selectedBrand ? optionTile : null}

          {hubStep === 'amount' ? (
            <section className={`${styles.card} ${styles.enter}`} aria-labelledby="account-hub-cashout-amount">
              <div className={styles.available}>
                <div className={styles.availableMain}>
                  <span className={styles.eyebrow}>{intl.formatMessage({ id: 'wallet.cashout.balance.cashable' })}</span>
                  <span className={styles.availableValue}>{formatPoints(cashable)}</span>
                </div>
                {balanceFacts}
                <p className={styles.availableNote}>{balanceHint}</p>
              </div>
              <h2 id="account-hub-cashout-amount" className={styles.cardTitle}>
                {intl.formatMessage({ id: 'wallet.cashout.amount.label' })}
              </h2>
              <div className={styles.amounts}>
                {amountChips.map(value => (
                  <button
                    key={value}
                    type="button"
                    className={amount === value ? `${styles.amountChip} ${styles.amountChipActive}` : styles.amountChip}
                    aria-pressed={amount === value}
                    onClick={() => commitAmount(value)}
                  >
                    {formatMoney(value, currency)}
                  </button>
                ))}
                <div className={isOtherAmount ? `${styles.otherAmount} ${styles.otherAmountActive}` : styles.otherAmount}>
                  <span aria-hidden="true">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    placeholder={intl.formatMessage({ id: 'wallet.cashout.amount.placeholder' })}
                    aria-label={intl.formatMessage({ id: 'wallet.cashout.amount.placeholder' })}
                    value={isOtherAmount ? amount : ''}
                    onChange={event => setAmountKeepingOption(event.target.value === '' ? null : Number(event.target.value))}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        confirmTypedAmount();
                      }
                    }}
                  />
                  {allAmount != null ? (
                    <button
                      type="button"
                      className={styles.useAll}
                      aria-pressed={amount === allAmount}
                      onClick={() => commitAmount(allAmount)}
                    >
                      {intl.formatMessage({ id: 'my.account.hub.cashout.use_all' }, { amount: formatMoney(allAmount, currency) })}
                    </button>
                  ) : null}
                </div>
              </div>
              {amountError ? <p className={styles.error} role="alert">{amountError}</p> : null}
              {amountBreakdown ? <p className={styles.breakdown}>{amountBreakdown}</p> : null}
              {isOtherAmount && !amountError ? (
                <div className={styles.stepFooter}>
                  <button type="button" className={styles.primaryButton} onClick={confirmTypedAmount}>
                    {intl.formatMessage({ id: 'wallet.cashout.continue' })}
                  </button>
                </div>
              ) : null}
              <p className={styles.hint}>
                {options.minAmount != null
                  ? `${intl.formatMessage({ id: 'wallet.cashout.amount.min_hint' }, { min: formatMoney(options.minAmount, currency) })} `
                  : ''}
                {amountBreakdown ? null : feeHint}
              </p>
            </section>
          ) : null}

          {hubStep === 'option' ? (
            <section className={`${styles.card} ${styles.rewardCard} ${styles.enter}`} aria-labelledby="account-hub-cashout-brand">
              <div className={styles.rewardBody}>
                <div className={styles.cardHeader}>
                  <h2 id="account-hub-cashout-brand" className={styles.cardTitle}>
                    {intl.formatMessage({ id: 'my.account.hub.cashout.reward_title' })}
                  </h2>
                  {brandsForAmount.length > SEARCH_THRESHOLD ? (
                    <div className={styles.search}>
                      <Input
                        allowClear
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />}
                        placeholder={intl.formatMessage({ id: 'wallet.cashout.brand.search' }, { count: brandsForAmount.length })}
                        aria-label={intl.formatMessage({ id: 'wallet.cashout.brand.search' }, { count: brandsForAmount.length })}
                      />
                    </div>
                  ) : null}
                </div>
                {!brandsForAmount.length ? <p className={styles.hint}>{intl.formatMessage({ id: 'wallet.cashout.brand.none' })}</p> : null}
                {brandsForAmount.length && !sections.length ? (
                  <p className={styles.hint}>{intl.formatMessage({ id: 'wallet.cashout.brand.no_match' })}</p>
                ) : null}
                {sections.map(({ group, brands }) => {
                  const isExpanded = !!expanded[group] || !!search.trim();
                  const visible = isExpanded
                    ? brands
                    : brands.filter((brand, index) => index < COLLAPSED_COUNT || brand.productId === productId);
                  const hidden = brands.length - visible.length;
                  return (
                    <div key={group} className={styles.brandGroup}>
                      <h3 className={styles.groupTitle}>
                        {intl.formatMessage({ id: GROUP_LABELS[group] })}
                        <span>{brands.length}</span>
                      </h3>
                      <ul className={styles.brands}>
                        {visible.map(brand => {
                          const isSelected = brand.productId === productId;
                          return (
                            <li key={brand.productId}>
                              <button
                                type="button"
                                className={isSelected ? `${styles.brand} ${styles.brandSelected}` : styles.brand}
                                aria-pressed={isSelected}
                                disabled={isBusy}
                                onClick={() => pickOption(brand.productId)}
                              >
                                <BrandLogo brand={brand} />
                                <span className={styles.brandText}>
                                  <span className={styles.brandName}>{brand.name}</span>
                                  {brand.minAmount != null || brand.maxAmount != null ? (
                                    <span className={styles.brandMeta}>
                                      {[brand.minAmount, brand.maxAmount].filter(value => value != null)
                                        .map(value => formatMoney(value as number, currency)).join(' – ')}
                                    </span>
                                  ) : null}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                      {hidden > 0 ? (
                        <button type="button" className={styles.linkButton} onClick={() => setExpanded(current => ({ ...current, [group]: true }))}>
                          {intl.formatMessage({ id: 'wallet.cashout.brand.more' }, { count: hidden })}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {isBusy ? (
                <p className={styles.quoting} role="status">
                  <span className={styles.spinner} aria-hidden="true" />
                  {intl.formatMessage({ id: 'my.account.hub.cashout.loading' })}
                </p>
              ) : null}
            </section>
          ) : null}

          {hubStep === 'review' && quote ? (
            <section className={`${styles.card} ${styles.review} ${styles.enter}`} aria-labelledby="account-hub-cashout-review">
              <div className={styles.reviewHero}>
                <BrandLogo brand={selectedBrand} />
                <h2 id="account-hub-cashout-review" className={styles.reviewName}>{selectedBrand?.name ?? productId}</h2>
                <div className={styles.reviewPayout}>
                  <b>{formatMoney(quote.payoutAmount, currency)}</b>
                  <span>{intl.formatMessage({ id: 'wallet.cashout.confirm.gift_card' })}</span>
                </div>
              </div>
              {selectedBrand?.description ? (
                <div className={styles.reviewAbout}>
                  <b>{intl.formatMessage({ id: 'my.account.hub.cashout.about' })}</b>
                  <p className={styles.reviewDescription}>{selectedBrand.description}</p>
                </div>
              ) : null}
              <p className={styles.hint}>
                {intl.formatMessage(
                  { id: quote.feeMode === 'Gross' ? 'wallet.cashout.confirm.gross' : 'wallet.cashout.confirm.net' },
                  { debited: formatPoints(quote.pointsDebited), giftCard: formatMoney(quote.payoutAmount, currency) },
                )}
              </p>
              {!options.requiresVerification ? finalWarning : null}
              {options.requiresVerification ? (
                <div className={styles.security} role="note">
                  <span className={styles.securityIcon} aria-hidden="true"><FontAwesomeIcon icon={faShieldHalved} /></span>
                  <div>
                    <b>{intl.formatMessage({ id: 'my.account.hub.cashout.security.title' })}</b>
                    <span>
                      {intl.formatMessage(
                        { id: maskedOwnerEmail ? 'my.account.hub.cashout.security.text' : 'my.account.hub.cashout.security.text_no_email' },
                        { email: <b key="email">{maskedOwnerEmail}</b> },
                      )}
                    </span>
                  </div>
                </div>
              ) : null}
              <div className={styles.actions}>
                <button type="button" className={styles.secondaryButton} disabled={isBusy} onClick={editOption}>
                  {intl.formatMessage({ id: 'wallet.cashout.back' })}
                </button>
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={isBusy || (!options.requiresVerification && !agreed)}
                  onClick={options.requiresVerification ? sendCode : handleConfirm}
                >
                  {options.requiresVerification ? <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" /> : null}
                  {intl.formatMessage({ id: options.requiresVerification ? 'my.account.hub.cashout.security.send' : 'wallet.cashout.confirm.submit' })}
                </button>
              </div>
            </section>
          ) : null}

          {step === 'verify' && challenge ? (
            <section className={`${styles.card} ${styles.centered} ${styles.enter}`} aria-labelledby="account-hub-cashout-verify">
              <span className={styles.iconCircle} aria-hidden="true"><FontAwesomeIcon icon={faEnvelope} /></span>
              <h2 id="account-hub-cashout-verify" className={styles.centeredTitle}>{intl.formatMessage({ id: 'wallet.cashout.verify.title' })}</h2>
              <p className={styles.centeredBody}>{intl.formatMessage({ id: 'wallet.cashout.verify.sent' }, { email: challenge.sentTo ?? maskedOwnerEmail ?? '' })}</p>
              <p className={styles.hint}>{intl.formatMessage({ id: 'my.account.hub.cashout.security.verify_hint' })}</p>
              <CodeBoxes
                value={code}
                onChange={setCode}
                onSubmit={() => {
                  if (agreed) {
                    handleConfirm();
                  }
                }}
                label={intl.formatMessage({ id: 'wallet.cashout.verify.title' })}
              />
              {/* Submitting the code is what issues the reward, so the can't-undo warning sits here. */}
              <div className={styles.verifyFinal}>{finalWarning}</div>
              <button type="button" className={`${styles.primaryButton} ${styles.wide}`} disabled={code.length !== CODE_LENGTH || !agreed || isBusy} onClick={handleConfirm}>
                {intl.formatMessage(
                  { id: 'my.account.hub.cashout.complete' },
                  { amount: quote ? formatMoney(quote.requestedAmount, currency) : '' },
                )}
              </button>
              <button type="button" className={styles.linkButton} disabled={isBusy || resendIn > 0} onClick={sendCode}>
                {resendIn > 0
                  ? intl.formatMessage({ id: 'wallet.cashout.verify.resend_in' }, { seconds: resendIn })
                  : intl.formatMessage({ id: 'wallet.cashout.verify.resend' })}
              </button>
            </section>
          ) : null}

          {step === 'result' && detail ? (
            <section className={`${styles.card} ${styles.centered} ${styles.resultCard} ${styles.enter}`} ref={resultRef} aria-labelledby="account-hub-cashout-result">
              {/* A delivered reward gets a short celebration, once per cash-out. */}
              {statusTone(detail.status) === 'delivered' ? <Confetti key={detail.cashOutCode} /> : null}
              {(() => {
                const tone = statusTone(detail.status);
                const icon = tone === 'delivered' ? faCircleCheck : tone === 'failed' ? faCircleExclamation : faClock;
                return (
                  <span className={`${styles.iconCircle} ${styles[`icon_${tone}`]}`} aria-hidden="true"><FontAwesomeIcon icon={icon} /></span>
                );
              })()}
              <h2 id="account-hub-cashout-result" className={styles.centeredTitle}>{intl.formatMessage({ id: 'wallet.cashout.result.title' })}</h2>
              <StatusBadge status={detail.status} />
              <span className={styles.resultAmount}>{formatMoney(detail.payoutAmount, detail.currencyCode)}</span>
              <span className={styles.centeredBody}>{brandName(detail.productId)}</span>
              {detail.redemptionLink ? (
                <>
                  <button type="button" className={`${styles.primaryButton} ${styles.wide}`} onClick={() => handleRedeem(detail.redemptionLink as string)}>
                    {intl.formatMessage({ id: 'wallet.cashout.result.redeem' })}
                  </button>
                  <p className={styles.hint}>{intl.formatMessage({ id: 'wallet.cashout.result.redeem.hint' })}</p>
                </>
              ) : null}
              {!TERMINAL_STATUSES.includes(detail.status) ? (
                <p className={styles.hint}>{intl.formatMessage({ id: 'wallet.cashout.result.processing' })}</p>
              ) : null}
              {FAILED_STATUSES.includes(detail.status) ? (
                <p className={styles.hint}>{intl.formatMessage({ id: 'wallet.cashout.result.failed' })}</p>
              ) : null}
              {/* Where to find it again. The delivery email is off by default on the back end
                  (WalletCashOut:SendDeliveryEmail), so this points to the history, not the inbox. */}
              {!FAILED_STATUSES.includes(detail.status) ? (
                <p className={styles.emailNote}>
                  <FontAwesomeIcon icon={faClockRotateLeft} aria-hidden="true" />
                  <span>
                    {intl.formatMessage(
                      { id: statusTone(detail.status) === 'delivered' ? 'my.account.hub.cashout.saved' : 'my.account.hub.cashout.saved_pending' },
                      { reward: brandName(detail.productId) },
                    )}
                  </span>
                </p>
              ) : null}
              <div className={styles.actions}>
                {!TERMINAL_STATUSES.includes(detail.status) ? (
                  <button type="button" className={styles.secondaryButton} disabled={isBusy} onClick={() => openDetail(detail.cashOutCode)}>
                    {intl.formatMessage({ id: 'wallet.cashout.result.refresh' })}
                  </button>
                ) : null}
                {/* Redeem is the main action when there's a link; starting over is secondary then. */}
                <button
                  type="button"
                  className={detail.redemptionLink ? styles.secondaryButton : styles.primaryButton}
                  disabled={isBusy}
                  onClick={startOver}
                >
                  {intl.formatMessage({ id: 'wallet.cashout.result.new' })}
                </button>
              </div>
            </section>
          ) : null}
        </div>

        {summary ? (
          <div className={styles.sideColumn}>
            {summary}
            {hubStep === 'done' ? recentCard : null}
          </div>
        ) : null}
      </div>

      {hubStep === 'amount' && history.length ? recentCard : null}
      {/* First cash-out: what to expect, where the recent list will be once there is history. */}
      {hubStep === 'amount' && !history.length ? (
        <div className={styles.firstTime} role="note">
          <span className={styles.securityIcon} aria-hidden="true"><FontAwesomeIcon icon={faGift} /></span>
          <div>
            <b>{intl.formatMessage({ id: 'my.account.hub.cashout.first.title' })}</b>
            <span>{intl.formatMessage({ id: 'my.account.hub.cashout.first.text' })}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};
